import { Injectable } from '@angular/core';

import {
  approveAccessRequest,
  getAccessRequestsForCurrentActor,
  getApprovedEmailDomainsByCustomer,
  getAssignableAffiliatesForCurrentAdmin,
  getDiscoveryUser,
  getPendingApprovalsCountForCurrentActor,
  getPresidioDiscoveryState,
  getPurchaseRuleCheckoutNotice,
  getSessionAssignedAffiliates as loadSessionAssignedAffiliates,
  rejectAccessRequest,
  savePresidioDiscoveryState,
  searchDiscoverySkus,
  setAddressHidden,
  setApprovedEmailDomainsForCustomer,
  upsertDiscoveryUser,
  type PresidioApprovedEmailDomainChangeNotice,
} from '../../presidio-demo/mock/presidio-mock-discovery';
import {
  parseApprovedEmailDomains,
  validateEmailAgainstCustomerDomains,
  type PresidioEmailDomainValidationResult,
} from '../utils/presidio-approved-email-domains';
import { wouldLeaveAccountWithoutAdmin } from '../utils/presidio-last-admin';
import {
  getPresidioMockActiveCustomerNo,
  getPresidioMockActiveLogin,
} from '../../presidio-demo/mock/presidio-mock-session';
import {
  PRESIDIO_DISCOVERY_AFFILIATES,
  type PresidioDiscoveryAccessRequest,
  type PresidioDiscoveryAddressMeta,
  type PresidioDiscoveryAffiliate,
  type PresidioDiscoveryPurchaseRule,
  type PresidioDiscoverySkuOption,
  type PresidioDiscoveryUser,
} from '../models/presidio-discovery.model';

/**
 * DEMO-ONLY: wraps sessionStorage-backed discovery mock for UI pages.
 * Stakeholder-approved discovery UX — DEMO-ONLY mock; not production ICM.
 */
@Injectable({ providedIn: 'root' })
export class PresidioDiscoveryService {
  getUsers(): PresidioDiscoveryUser[] {
    return [...getPresidioDiscoveryState().users];
  }

  getUser(login: string): PresidioDiscoveryUser | undefined {
    return getDiscoveryUser(login);
  }

  getSessionUser(): PresidioDiscoveryUser | undefined {
    return getDiscoveryUser();
  }

  getAccessRequests(): PresidioDiscoveryAccessRequest[] {
    return [...getAccessRequestsForCurrentActor()];
  }

  /** Combined pending order + user-access approvals for header Approvals badge. */
  getPendingApprovalsCount(): number {
    return getPendingApprovalsCountForCurrentActor();
  }

  getAccessRequest(id: string): PresidioDiscoveryAccessRequest | undefined {
    return getAccessRequestsForCurrentActor().find(r => r.id === id);
  }

  getAssignableAffiliates(): PresidioDiscoveryAffiliate[] {
    return getAssignableAffiliatesForCurrentAdmin();
  }

  /** Approved email domains for every affiliate the current admin can manage. */
  getApprovedEmailDomainsForAssignableAffiliates(): {
    affiliate: PresidioDiscoveryAffiliate;
    domains: string[];
  }[] {
    const map = getApprovedEmailDomainsByCustomer();
    return this.getAssignableAffiliates().map(affiliate => ({
      affiliate,
      domains: [...(map[affiliate.customerNo] || [])],
    }));
  }

  setApprovedEmailDomains(
    customerNo: string,
    domains: string[] | string
  ): PresidioApprovedEmailDomainChangeNotice[] {
    return setApprovedEmailDomainsForCustomer(customerNo, parseApprovedEmailDomains(domains));
  }

  /**
   * Validate a create/approve email against approved domains for the accounts
   * the user is being assigned. Empty affiliate list → check active/home account only.
   */
  validateUserEmailForAffiliates(
    email: string,
    affiliateCustomerNos: string[]
  ): PresidioEmailDomainValidationResult {
    const customerNos =
      affiliateCustomerNos?.length > 0
        ? affiliateCustomerNos
        : [this.getActiveCustomerNo()];
    return validateEmailAgainstCustomerDomains(email, customerNos, getApprovedEmailDomainsByCustomer());
  }

  /** Affiliates assigned to the logged-in user (restrict My Account filter options). */
  getSessionAssignedAffiliates(): PresidioDiscoveryAffiliate[] {
    return loadSessionAssignedAffiliates();
  }

  /**
   * Active users who share any affiliate with the session user's assigned accounts
   * (work-item Requester filter — MVP 6.3.1 / 8.2.1).
   */
  getUsersAcrossSessionAssignedAffiliates(): PresidioDiscoveryUser[] {
    const assigned = new Set(this.getSessionAssignedAffiliates().map(a => a.customerNo));
    const users = this.getUsers().filter(user => {
      if (String(user.status || 'Active').toLowerCase() !== 'active') {
        return false;
      }
      if (!assigned.size) {
        return true;
      }
      return (user.affiliateCustomerNos || []).some(customerNo => assigned.has(customerNo));
    });
    return users.sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, undefined, {
        sensitivity: 'base',
      })
    );
  }

  getApproverCandidates(excludeLogin?: string): PresidioDiscoveryUser[] {
    return this.getUsers().filter(
      u =>
        u.canApprove &&
        u.status === 'Active' &&
        (!excludeLogin || u.login.toLowerCase() !== excludeLogin.toLowerCase())
    );
  }

  getPurchaseRulesForUser(login: string): PresidioDiscoveryPurchaseRule[] {
    return getPresidioDiscoveryState().purchaseRules.filter(
      r => r.userLogin.toLowerCase() === login.toLowerCase()
    );
  }

  /** MVP 1.2.4 — buyer “Orders awaiting approval” only when this user has purchase rules. */
  sessionUserHasPurchaseRules(): boolean {
    const user = this.getSessionUser();
    if (!user?.login) {
      return false;
    }
    return this.getPurchaseRulesForUser(user.login).length > 0;
  }

  /**
   * Persist a user. Returns false when the change would leave an ERP account
   * without an Active administrator (MVP 1.2.1 / FR18.1.9).
   */
  upsertUser(user: PresidioDiscoveryUser): boolean {
    const guard = wouldLeaveAccountWithoutAdmin(this.getUsers(), user, this.getActiveCustomerNo());
    if (guard.blocked) {
      return false;
    }
    upsertDiscoveryUser(user);
    return true;
  }

  /** True when deactivating this user would leave an account without an Active admin. */
  cannotDeactivateLastAdmin(user: PresidioDiscoveryUser): boolean {
    return wouldLeaveAccountWithoutAdmin(
      this.getUsers(),
      { ...user, status: 'Disabled' },
      this.getActiveCustomerNo()
    ).blocked;
  }

  /** True when demoting this user from Administrator would leave an account without an Active admin. */
  cannotDemoteLastAdmin(user: PresidioDiscoveryUser): boolean {
    return wouldLeaveAccountWithoutAdmin(
      this.getUsers(),
      { ...user, primaryRole: 'standard' },
      this.getActiveCustomerNo()
    ).blocked;
  }

  setPurchaseRulesForUser(login: string, rules: Omit<PresidioDiscoveryPurchaseRule, 'id' | 'userLogin'>[]): void {
    const state = getPresidioDiscoveryState();
    const kept = state.purchaseRules.filter(r => r.userLogin.toLowerCase() !== login.toLowerCase());
    const next: PresidioDiscoveryPurchaseRule[] = rules.map((r, i) => ({
      id: `PR-${login}-${i + 1}-${Date.now()}`,
      userLogin: login,
      type: r.type,
      threshold: r.threshold,
      sku: r.sku,
    }));
    state.purchaseRules = [...kept, ...next];
    savePresidioDiscoveryState(state);
  }

  approveAccessRequest(
    id: string,
    overlay?: Partial<
      Pick<
        PresidioDiscoveryUser,
        | 'affiliateCustomerNos'
        | 'primaryRole'
        | 'canApprove'
        | 'firstName'
        | 'lastName'
        | 'approverLogin'
        | 'department'
        | 'title'
        | 'phone'
      >
    >
  ): void {
    approveAccessRequest(id, overlay);
  }

  rejectAccessRequest(id: string, comments?: string): void {
    rejectAccessRequest(id, comments);
  }

  getAddressesForActiveCustomer(): PresidioDiscoveryAddressMeta[] {
    // Administrators with affiliates: Manage Addresses shows all assigned affiliates
    // (MVP 8.2.1) — not only the active shopping account.
    const sessionUser = getDiscoveryUser();
    if (sessionUser?.primaryRole === 'administrator' && sessionUser.affiliateCustomerNos?.length) {
      const allowed = new Set(sessionUser.affiliateCustomerNos);
      return getPresidioDiscoveryState().addressMeta.filter(a => allowed.has(a.customerNo));
    }
    const customerNo = getPresidioMockActiveCustomerNo() || 'PRESIDIO-MOCK';
    return getPresidioDiscoveryState().addressMeta.filter(a => a.customerNo === customerNo);
  }

  setAddressHidden(addressMetaId: string, hidden: boolean): void {
    setAddressHidden(addressMetaId, hidden);
  }

  getActiveCustomerNo(): string {
    return getPresidioMockActiveCustomerNo() || 'PRESIDIO-MOCK';
  }

  affiliateLabel(customerNo: string): string {
    return PRESIDIO_DISCOVERY_AFFILIATES.find(a => a.customerNo === customerNo)?.companyName || customerNo;
  }

  /** Account-catalog SKU search for purchase-rule configuration. */
  searchSkus(query: string, exclude: string[] = []): PresidioDiscoverySkuOption[] {
    return searchDiscoverySkus(query, exclude);
  }

  /**
   * DEMO MVP 1.2.4 — evaluate current session user's cart against purchase rules
   * for the checkout approval notice.
   */
  getCheckoutPurchaseRuleNotice(input: {
    subtotal: number;
    lines: { sku: string; unitPrice: number }[];
    /** FR10.10.2 — quote carts ignore purchase rules. */
    hasQuoteLines?: boolean;
  }): { triggered: boolean; reasons: string[]; approverDisplayName?: string } {
    const login = getPresidioMockActiveLogin();
    if (!login) {
      return { triggered: false, reasons: [] };
    }
    return getPurchaseRuleCheckoutNotice({
      userLogin: login,
      subtotal: input.subtotal,
      lines: input.lines,
      hasQuoteLines: input.hasQuoteLines,
    });
  }
}
